import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Select, type OptionType } from "../Select";
import type { TimezoneOption } from "@core/features/timezones/timezones.types";

export type { TimezoneOption };

export type Props = {
  /** Field name for identification */
  name: string;
  /** Available timezone options */
  options: TimezoneOption[];
  /** Currently selected timezone(s) */
  value?: TimezoneOption | TimezoneOption[] | null;
  /** Change handler - receives selected timezone(s) */
  onChange?: (val: TimezoneOption | TimezoneOption[] | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether multiple timezones can be selected */
  isMulti?: boolean;
  /** Whether new timezones can be created */
  isCreatable?: boolean;
  /** Handler for creating new timezone options */
  onCreate?: (input: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the select is loading */
  isLoading?: boolean;
  /** Whether field has an icon (affects padding) */
  isWithIcon?: boolean;
  /** Size variant */
  size?: "default" | "lg";
  /** Additional CSS classes */
  className?: string;
  /** ID of the element that labels this select (for accessibility) */
  "aria-labelledby"?: string;
};

/**
 * Styled timezone select component.
 * Pure UI component - works with or without react-hook-form.
 *
 * This is a specialized Select component for timezone selection.
 * Pass timezone options from useTimezoneOptions hook or your own list.
 *
 * @example
 * // Basic usage with timezone options
 * const { timezones } = useTimezoneOptions();
 *
 * <TimezoneSelect
 *   name="timezone"
 *   options={timezones}
 *   value={selectedTimezone}
 *   onChange={setSelectedTimezone}
 *   placeholder="Select timezone"
 * />
 *
 * @example
 * // With FormField (includes label and error display)
 * import { FormField } from "@themes/pollinator/ui/FormField";
 * import { Form } from "@themes/pollinator/ui/Form";
 *
 * <Form form={form}>
 *   <FormField name="timezone" label="Timezone" component="TimezoneSelect" />
 * </Form>
 */
export const TimezoneSelect = forwardRef<any, Props>(
  (
    {
      name,
      options,
      value,
      onChange,
      onBlur,
      error,
      disabled = false,
      isMulti = false,
      isCreatable = false,
      onCreate,
      placeholder = "Select timezone...",
      isLoading = false,
      isWithIcon = false,
      size,
      className,
      "aria-labelledby": ariaLabelledBy,
    },
    ref
  ) => {
    return (
      <div className={twMerge("border-none", className)}>
        <Select
          ref={ref}
          name={name}
          options={options}
          value={value}
          onChange={onChange as ((val: OptionType | OptionType[] | null) => void) | undefined}
          onBlur={onBlur}
          error={error}
          disabled={disabled}
          isMulti={isMulti}
          isCreatable={isCreatable}
          onCreate={onCreate}
          placeholder={placeholder}
          isLoading={isLoading}
          isWithIcon={isWithIcon}
          size={size}
          aria-labelledby={ariaLabelledBy}
        />
      </div>
    );
  }
);

TimezoneSelect.displayName = "TimezoneSelect";

export default TimezoneSelect;

